import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * @typedef {Object} NavigationButtonProps
 * @property {string} label - Button label text
 * @property {() => void} onClick - Click handler function
 * @property {string} [className] - Additional CSS classes
 * @property {Object} [sx] - Additional Material-UI sx props
 * @property {boolean} [isMobile] - Whether the button is in mobile context
 * @property {boolean} [isActive] - Whether this nav item is currently active
 */

/**
 * Navigation Button component
 *
 * A reusable navigation button with consistent styling for the EllaFin
 * header navigation. Supports both desktop and mobile contexts.
 * Shows a primary-colored underline when the navigation item is active.
 * All colors are sourced from the MUI theme for theme switching support.
 *
 * @param {NavigationButtonProps} props - Component props
 * @returns {JSX.Element} Navigation button
 */
function NavigationButton({
  label,
  onClick,
  className,
  sx = {},
  isMobile = false,
  isActive = false,
}) {
  const theme = useTheme();

  return (
    <Button
      color="inherit"
      onClick={onClick}
      className={className}
      sx={{
        px: isMobile ? 2 : 1.5,
        py: isMobile ? 1 : 1.5,
        fontSize: '1rem',
        fontWeight: 500,
        color: 'inherit',
        textTransform: 'none',
        borderRadius: '9999px',
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        // Active state underline
        '&::after': isActive
          ? {
              content: '""',
              position: 'absolute',
              bottom: '8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'calc(100% - 24px)',
              height: '2px',
              backgroundColor: 'currentColor',
              borderRadius: '1px',
            }
          : {},
        '&:hover': {
          color: theme.palette.secondary.main,
          backgroundColor: 'transparent',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        ...sx,
      }}
    >
      {label}
    </Button>
  );
}

export default NavigationButton;
